import { AimSettingsInput, cmPer360, edpi, type AimSettings } from "@69ly/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { errorText } from "../lib/errors.ts";
import { useSaveAim } from "../lib/leaderboard-queries.ts";

/** Up to three decimals, no trailing zeros: 1.25, 0.8, 2. */
const plain = (n: number) => String(Math.round(n * 1000) / 1000);

/** My mouse settings, in Settings › My account (0.23.0; before, on the Leaderboard's Aim tab). */
export function AimForm({ saved, bare = false }: { saved: AimSettings; /** Inside another card (Settings): no card frame or title of its own. */ bare?: boolean }) {
  const { t } = useTranslation();
  const save = useSaveAim();
  const [dpi, setDpi] = useState(saved.dpi?.toString() ?? "");
  const [sens, setSens] = useState(saved.sensitivity?.toString() ?? "");
  const num = (v: string) => (v.trim() === "" ? null : Number(v.replace(",", ".")));
  const draft = { dpi: num(dpi), sensitivity: num(sens) };
  const check = AimSettingsInput.safeParse(draft);
  const e = check.success ? edpi(check.data) : null;
  const changed = draft.dpi !== saved.dpi || draft.sensitivity !== saved.sensitivity;
  return (
    <form
      className={bare ? "aim-form is-bare" : "card aim-form"}
      onSubmit={(ev) => {
        ev.preventDefault();
        if (check.success) save.mutate(check.data);
      }}
    >
      {!bare && <h2 className="panel-title">{t("aim.mine")}</h2>}
      <div className="aim-fields">
        <label className="field">
          <span>{t("aim.dpi")}</span>
          <input inputMode="numeric" value={dpi} placeholder="800" onChange={(ev) => setDpi(ev.target.value)} aria-invalid={!check.success && dpi !== "" ? true : undefined} />
        </label>
        <span className="aim-times" aria-hidden="true">
          ×
        </span>
        <label className="field">
          <span>{t("aim.sens")}</span>
          <input inputMode="decimal" value={sens} placeholder="1.25" onChange={(ev) => setSens(ev.target.value)} />
        </label>
        <span className="aim-times" aria-hidden="true">
          =
        </span>
        <div className="aim-result">
          <span className="muted small">{t("leaderboard.col.edpi")}</span>
          <b>{e !== null ? plain(e) : "–"}</b>
          {e !== null && <span className="muted small">{t("aim.cm", { cm: cmPer360(e)!.toFixed(1) })}</span>}
        </div>
      </div>
      {!check.success && <p className="field-error small">{check.error.issues[0]?.message}</p>}
      {save.error && <p className="field-error small">{errorText(t, save.error)}</p>}
      <div className="row">
        <button type="submit" className="btn btn-primary btn-small" disabled={!check.success || !changed || save.isPending}>
          {t("aim.save")}
        </button>
        {save.isSuccess && !changed && <span className="muted small">{t("aim.saved")}</span>}
      </div>
      <p className="muted small">{t("aim.help")}</p>
    </form>
  );
}
